/* UnifiedDonationsLedgerTab — single reporting view across every donation channel.
 * Joins Stripe payments + Stripe merch + journal-entry gifts (grants, sponsorships,
 * checks, cash, wires, in-kind) + event-linked donations.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Heart, Loader2, RefreshCw, Search, FileSpreadsheet, Calendar, Users,
  TrendingUp, Mail, CheckCircle2,
} from 'lucide-react';
import FundPicker from '../../../components/admin/FundPicker';
import DonorProfileDrawer from '../../../components/admin/DonorProfileDrawer';
import { isoLocalDate, tzAbbrev } from '../../../utils/formatDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default filter dates use the platform display zone (ET) — not the
// admin's browser zone — so the ledger opens on the correct ET day.
const todayIso = () => isoLocalDate(new Date());
const firstOfYear = () => `${isoLocalDate(new Date()).slice(0, 4)}-01-01`;

const SOURCE_COLOR = {
  stripe: 'bg-blue-50 text-blue-800 border-blue-200',
  shop: 'bg-cyan-50 text-cyan-800 border-cyan-200',
  event_donation: 'bg-violet-50 text-violet-800 border-violet-200',
  grant: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  sponsorship: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  check_donation: 'bg-amber-50 text-amber-800 border-amber-200',
  cash_donation: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  wire_transfer: 'bg-rose-50 text-rose-800 border-rose-200',
  in_kind_donation: 'bg-pink-50 text-pink-800 border-pink-200',
};

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const UnifiedDonationsLedgerTab = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [count, setCount] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sources, setSources] = useState([]);
  // Track per-row resend state so a click on one row's button doesn't
  // spin every row's button. `null | 'sending' | 'sent' | 'error:<msg>'`.
  const [resendState, setResendState] = useState({});
  const [filters, setFilters] = useState({
    date_from: firstOfYear(),
    date_to: todayIso(),
    source: [],
    fund_id: '',
    tax_deductible: '',
    search: '',
  });
  const [profileDonor, setProfileDonor] = useState(null);  // {name, email}

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/donations/unified-ledger/sources`, { withCredentials: true })
      .then((r) => setSources(r.data?.sources || []))
      .catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.date_from) p.set('date_from', filters.date_from);
    if (filters.date_to)   p.set('date_to', filters.date_to);
    if (filters.source.length) p.set('source', filters.source.join(','));
    if (filters.fund_id)   p.set('fund_id', filters.fund_id);
    if (filters.tax_deductible) p.set('tax_deductible', filters.tax_deductible);
    if (filters.search)    p.set('search', filters.search);
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/donations/unified-ledger?${buildParams()}`,
        { withCredentials: true });
      setRows(r.data?.rows || []);
      setSummary(r.data?.summary || {});
      setCount(r.data?.count || 0);
      setTruncated(!!r.data?.truncated);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load donations.'); }
    setLoading(false);
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const toggleSource = (id) => {
    setFilters((f) => ({
      ...f,
      source: f.source.includes(id) ? f.source.filter((x) => x !== id) : [...f.source, id],
    }));
  };

  const downloadCsv = () => {
    window.open(`${BACKEND_URL}/api/admin/donations/unified-ledger.csv?${buildParams()}`, '_blank');
  };

  // Resend the receipt email for a Stripe/Shop donation. Uses the row's
  // stable `id` (payment_transactions.id). Works for donations from ANY
  // date — the endpoint has no age gate.
  const resendReceipt = async (row) => {
    if (!row?.id) return;
    // If the donor has no email on file, prompt the admin to enter one.
    let overrideEmail = null;
    const onFile = (row.donor_email || '').trim();
    if (!onFile) {
      const entered = window.prompt(
        `${row.donor_name || 'This donor'} didn't leave an email with their ` +
        `donation. Enter the email you want to send this receipt to:`);
      if (!entered) return;
      overrideEmail = entered.trim();
      if (!overrideEmail.includes('@')) {
        alert('That doesn\'t look like a valid email.');
        return;
      }
    }
    setResendState((s) => ({ ...s, [row.id]: 'sending' }));
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/receipts/${row.id}/resend`,
        overrideEmail ? { email: overrideEmail } : {},
        { withCredentials: true });
      if (r.data?.status === 'sent') {
        setResendState((s) => ({ ...s, [row.id]: 'sent' }));
        setTimeout(() => setResendState((s) => {
          const n = { ...s }; delete n[row.id]; return n;
        }), 5000);
      } else {
        setResendState((s) => ({ ...s, [row.id]:
          'error:' + (r.data?.detail || 'Unknown error') }));
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Send failed';
      setResendState((s) => ({ ...s, [row.id]: 'error:' + msg }));
      setTimeout(() => setResendState((s) => {
        const n = { ...s }; delete n[row.id]; return n;
      }), 6000);
    }
  };

  return (
    <div className="space-y-4" data-testid="unified-ledger-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#BA1826]" /> Unified Donations Ledger
          </h2>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
            One reporting view across every channel NHF receives support through: Stripe donations,
            merchandise, event gifts, grants, sponsorships, checks, cash, wires, and in-kind
            contributions. Filter, search, and export for board reports, grant applications, donor
            acknowledgement, or tax-receipt reconciliation.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
            data-testid="ledger-refresh"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button onClick={downloadCsv}
            className="text-xs px-3 py-1.5 border border-[#0A0A0A] text-[#0A0A0A] rounded inline-flex items-center gap-1.5 hover:bg-[#FAFBFC]"
            data-testid="ledger-csv-btn">
            <FileSpreadsheet className="w-3 h-3" /> CSV
          </button>
          <button onClick={() => {
              const url = `${BACKEND_URL}/api/admin/exports/finance-workbook.xlsx`;
              fetch(url, { credentials: 'include' }).then(async (r) => {
                if (!r.ok) return;
                const blob = await r.blob();
                const m = /filename=([^;]+)/i.exec(r.headers.get('content-disposition') || '');
                const fname = (m ? m[1] : 'nhf-finance-workbook.xlsx').replace(/["']/g, '').trim();
                const u = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = u; a.download = fname;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(u);
              });
            }}
            className="text-xs px-3 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1.5 hover:bg-[#1F1F1F]"
            data-testid="ledger-xlsx-btn">
            <FileSpreadsheet className="w-3 h-3" /> Excel
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ledger-summary">
        <Tile icon={TrendingUp} label="Total in period" value={fmtMoney(summary.total_amount)} accent="brand" />
        <Tile icon={Heart} label="Tax-deductible" value={fmtMoney(summary.deductible_amount)} accent="green" />
        <Tile icon={Heart} label="Non-deductible" value={fmtMoney(summary.non_deductible_amount)} accent="amber" />
        <Tile icon={Users} label="Unique supporters" value={summary.donor_count || 0} accent="indigo" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 space-y-2" data-testid="ledger-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="w-3.5 h-3.5 text-[#BA1826]" />
          <input type="date" value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            className="text-xs px-2 py-1 border border-[#E5E7EB] rounded" data-testid="ledger-date-from" />
          <span className="text-xs text-[#9CA3AF]">→</span>
          <input type="date" value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            className="text-xs px-2 py-1 border border-[#E5E7EB] rounded" data-testid="ledger-date-to" />
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search donor / memo / event…"
              className="pl-7 pr-2 py-1 border border-[#E5E7EB] rounded text-xs w-60"
              data-testid="ledger-search" />
          </div>
          <div className="w-56"><FundPicker value={filters.fund_id}
            onChange={(v) => setFilters((f) => ({ ...f, fund_id: v || '' }))}
            ariaLabel="Filter by fund" /></div>
          <select value={filters.tax_deductible}
            onChange={(e) => setFilters((f) => ({ ...f, tax_deductible: e.target.value }))}
            className="text-xs px-2 py-1 border border-[#E5E7EB] rounded" data-testid="ledger-deductible-filter">
            <option value="">All tax status</option>
            <option value="true">Tax-deductible only</option>
            <option value="false">Non-deductible only</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-[#F1F5F9]" data-testid="ledger-source-chips">
          <span className="text-[10px] uppercase font-bold tracking-wider text-[#9CA3AF]">Sources:</span>
          {sources.map((s) => (
            <button key={s.id} onClick={() => toggleSource(s.id)}
              className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold transition-colors ${
                filters.source.includes(s.id)
                  ? 'bg-[#BA1826] text-white border-[#BA1826]'
                  : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#BA1826]/40'
              }`}
              data-testid={`ledger-source-${s.id}`}>
              {s.label}
            </button>
          ))}
          {filters.source.length > 0 && (
            <button onClick={() => setFilters((f) => ({ ...f, source: [] }))}
              className="text-[10px] text-[#9CA3AF] underline ml-1"
              data-testid="ledger-source-clear">Clear</button>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>}

      {/* Rows */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center text-sm text-[#9CA3AF]"
          data-testid="ledger-empty">
          No donations in this period or filter.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="ledger-table">
              <thead className="bg-[#F8F9FA] text-[#9CA3AF]">
                <tr>
                  <th className="text-left px-3 py-2 w-24" title={`Time zone: ${tzAbbrev()}`}>Date ({tzAbbrev()})</th>
                  <th className="text-left px-3 py-2">Donor</th>
                  <th className="text-left px-3 py-2 w-32">Source</th>
                  <th className="text-left px-3 py-2 w-32">Payment</th>
                  <th className="text-left px-3 py-2 w-40">Fund</th>
                  <th className="text-left px-3 py-2 hidden lg:table-cell">Event / Memo</th>
                  <th className="text-center px-3 py-2 w-20">Tax</th>
                  <th className="text-right px-3 py-2 w-24">Amount</th>
                  <th className="text-center px-3 py-2 w-24">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.source}-${r.id || i}`} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]"
                    data-testid={`ledger-row-${i}`}>
                    <td className="px-3 py-2 font-mono">{r.date || '—'}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => setProfileDonor({ name: r.donor_name, email: r.donor_email })}
                        className="text-left hover:underline focus:outline-none focus:ring-2 focus:ring-[#BA1826]/40 rounded -mx-1 px-1"
                        data-testid={`ledger-donor-${i}`}>
                        <p className="font-semibold text-[#0A0A0A] truncate max-w-xs">{r.donor_name}</p>
                        {r.donor_email && <p className="text-[10px] text-[#9CA3AF] truncate">{r.donor_email}</p>}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${SOURCE_COLOR[r.source] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                        {r.source_label || r.source}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[#4B5563]">{r.payment_method || '—'}</td>
                    <td className="px-3 py-2">
                      {r.fund_label ? (
                        <span className="text-[10px] font-semibold text-[#0A0A0A]">{r.fund_label}</span>
                      ) : (
                        <span className="text-[10px] italic text-[#9CA3AF]">Unassigned</span>
                      )}
                    </td>
                    <td className="px-3 py-2 hidden lg:table-cell text-[#4B5563] truncate max-w-xs">
                      {r.event_label && <span className="font-semibold">{r.event_label} · </span>}
                      <span className="text-[10px]">{r.memo || r.reference || ''}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${
                        r.tax_deductible ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {r.tax_deductible ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{fmtMoney(r.amount)}</td>
                    <td className="px-3 py-2 text-center">
                      {(r.source === 'stripe' || r.source === 'shop') && r.id ? (
                        (() => {
                          const st = resendState[r.id];
                          const sending = st === 'sending';
                          const sent = st === 'sent';
                          const err = typeof st === 'string' && st.startsWith('error:');
                          return (
                            <button
                              type="button"
                              onClick={() => resendReceipt(r)}
                              disabled={sending}
                              title={sent ? 'Sent — click again to resend'
                                : err ? st.slice(6)
                                : r.donor_email
                                  ? `Email a fresh receipt link to ${r.donor_email}`
                                  : 'No email on file — click to enter one and send'}
                              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded border transition-colors ${
                                sent ? 'bg-green-50 text-green-700 border-green-200'
                                : err ? 'bg-red-50 text-red-700 border-red-200'
                                : 'bg-white text-[#BA1826] border-[#BA1826]/40 hover:bg-[#FEF2F2] disabled:opacity-40 disabled:cursor-not-allowed'
                              }`}
                              data-testid={`ledger-resend-receipt-${i}`}>
                              {sending ? <Loader2 className="w-3 h-3 animate-spin" />
                                : sent ? <CheckCircle2 className="w-3 h-3" />
                                : <Mail className="w-3 h-3" />}
                              {sending ? 'Sending' : sent ? 'Sent' : err ? 'Retry' : 'Resend'}
                            </button>
                          );
                        })()
                      ) : (
                        <span className="text-[10px] text-[#9CA3AF]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 bg-[#F8F9FA] border-t border-[#E5E7EB] flex items-center justify-between text-[11px] text-[#4B5563]"
            data-testid="ledger-footer">
            <span>{count} rows{truncated && ' (truncated — narrow your filters)'}</span>
            <span className="font-mono font-bold">Total {fmtMoney(summary.total_amount)}</span>
          </div>
        </div>
      )}

      <DonorProfileDrawer
        open={!!profileDonor}
        donorName={profileDonor?.name}
        donorEmail={profileDonor?.email}
        onClose={() => setProfileDonor(null)}
      />
    </div>
  );
};

const ACCENT = {
  green: 'bg-green-50 border-green-200 text-green-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  brand: 'bg-[#FFF8F8] border-[#BA1826]/30 text-[#BA1826]',
};

const Tile = ({ icon: Icon, label, value, accent = 'brand' }) => (
  <div className={`border rounded-lg p-3 ${ACCENT[accent] || ACCENT.brand}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</span>
      <Icon className="w-3.5 h-3.5 opacity-70" />
    </div>
    <p className="font-heading text-xl font-bold truncate" title={value}>{value}</p>
  </div>
);

export default UnifiedDonationsLedgerTab;
